import { FC, useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { Typography } from '@mui/material';
import { useRecoilState } from 'recoil';
import { TaskLabel } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import './LabelFilter.scss';
import {
  useTodoTasksContext,
  TaskFilterLabelNames,
  TaskFilterOptions,
  taskFilterAtom,
} from '../../../../data-access';

const LabelFilter: FC = () => {
  const { t } = useTranslation();
  const { taskLabels: allLabels } = useTodoTasksContext();
  const [taskFilter, setTaskFilter] = useRecoilState(taskFilterAtom);
  const [selectedLabels, setSelectedLabels] = useState<TaskLabel[]>([]);

  const handleFilterChange = useCallback(
    debounce((newVal: Partial<TaskFilterOptions>) => {
      setTaskFilter((prev) => ({ ...prev, ...newVal }));
    }, 300),
    []
  );

  useEffect(() => {
    if (taskFilter.labelNames === 'lifestack_all') {
      setSelectedLabels(allLabels);
    } else if (Array.isArray(taskFilter.labelNames)) {
      const labels = allLabels.filter((label) =>
        (taskFilter.labelNames as string[]).includes(label.name)
      );
      setSelectedLabels(labels);
    } else {
      setSelectedLabels([]);
    }
  }, [taskFilter.labelNames, allLabels]);

  const handleLabelToggle = (label: TaskLabel) => {
    const isSelected = selectedLabels.some((l) => l.name === label.name);
    let newSelectedLabels: TaskLabel[];

    if (isSelected) {
      newSelectedLabels = selectedLabels.filter((l) => l.name !== label.name);
    } else {
      newSelectedLabels = [...selectedLabels, label];
    }

    setSelectedLabels(newSelectedLabels);

    let newLabelNames: TaskFilterLabelNames = [];
    if (newSelectedLabels.length === allLabels.length) {
      newLabelNames = 'lifestack_all';
    } else if (newSelectedLabels.length > 0) {
      newLabelNames = newSelectedLabels.map((l) => l.name);
    }

    handleFilterChange({
      labelNames: newLabelNames,
    });
  };

  if (allLabels.length === 0) {
    return null;
  }

  return (
    <div className="label-filter">
      <Typography variant="subtitle2" className="label-filter__title">
        {t('task.filters.labels')}
      </Typography>
      <div className="label-filter__buttons">
        {allLabels.map((label) => {
          const isSelected = selectedLabels.some((l) => l.name === label.name);
          return (
            <button
              key={label.name}
              type="button"
              className={clsx('label-filter__button', {
                'label-filter__button--selected': isSelected,
              })}
              style={{
                borderColor: isSelected ? label.color : undefined,
              }}
              onClick={() => handleLabelToggle(label)}
            >
              <span
                className="label-filter__button__dot"
                style={{ backgroundColor: label.color }}
              />
              <Typography variant="subtitle2" className="label-filter__button__text">
                {label.name}
              </Typography>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LabelFilter;
